import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { LeaderboardEntry } from '../api/types';
import { useUser } from '../state/UserContext';

export function Leaderboard() {
  const { me, updateMe } = useUser();
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toggling, setToggling] = useState(false);

  const load = () => {
    setLoading(true);
    setError(null);
    api
      .getLeaderboard(20)
      .then((res) => setEntries(res.entries))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load the leaderboard.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleToggleOptIn = async () => {
    if (!me) return;
    setToggling(true);
    setError(null);
    try {
      await updateMe({ leaderboardOptIn: !me.leaderboardOptIn });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update your leaderboard setting.');
    } finally {
      setToggling(false);
    }
  };

  return (
    <div>
      <h1 className="page-title">Leaderboard</h1>
      <p className="page-subtitle">See how your streak compares &mdash; opt-in only.</p>

      <div className="toggle-row">
        <div>
          <div className="toggle-row-label">Show me on the leaderboard</div>
          <div className="practice-item-meta">Your display name and streak become visible to others.</div>
        </div>
        <button
          type="button"
          className={`switch${me?.leaderboardOptIn ? ' on' : ''}`}
          role="switch"
          aria-checked={Boolean(me?.leaderboardOptIn)}
          onClick={handleToggleOptIn}
          disabled={toggling || !me}
        >
          <span className="knob" />
        </button>
      </div>

      {error && <p className="error-text">{error}</p>}

      {loading ? (
        <div className="center-page" style={{ minHeight: '30vh' }}>
          <div className="spinner" />
        </div>
      ) : entries.length === 0 ? (
        <div className="empty-state">No one has opted in yet. Be the first!</div>
      ) : (
        <div className="leaderboard-list" style={{ marginTop: 20 }}>
          {entries.map((e) => (
            <div
              key={`${e.rank}-${e.displayName}`}
              className={`leaderboard-row${e.displayName === me?.displayName ? ' self' : ''}`}
            >
              <div className="leaderboard-rank">{e.rank}</div>
              <div className="leaderboard-name">{e.displayName}</div>
              <div className="leaderboard-streak">🔥 {e.currentStreak}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
